import type { Mutation, Query, QueryClient } from '@tanstack/react-query';
import { isPermissionRefused, type ApiError } from '@citibox-panel/api-client';
import type { Permission } from './access';
import {
  beginRecheck,
  getActiveView,
  getRecheckGeneration,
  isRecheckRunning,
  markViewRefused,
  settleRecheck,
} from './accessRecheck';

type AnyMutation = Mutation<unknown, unknown>;
type AnyQuery = Query<unknown, unknown>;

// Where a refusal came from: the view that made the request (null when none owns it), the session
// it was made in (absent: the current one) and, for a write, the refused mutation itself.
export interface RefusalSource {
  view: Permission | null;
  generation?: number;
  mutation?: AnyMutation;
}

// The view a request belongs to and the session it was made in. A change of person cannot abort a
// write already on the wire, so its answer can arrive in the next person's session.
interface RequestOrigin {
  view: Permission | null;
  generation: number;
}

const currentOrigin = (): RequestOrigin => ({
  view: getActiveView(),
  generation: getRecheckGeneration(),
});

const mutationOrigins = new WeakMap<AnyMutation, RequestOrigin>();

// A write belongs to the view that sent it, not to whatever is on screen when the answer arrives.
export function rememberMutationView(mutation: AnyMutation): void {
  mutationOrigins.set(mutation, currentOrigin());
}

export function viewOfMutation(mutation: AnyMutation): Permission | null {
  return mutationOrigins.get(mutation)?.view ?? null;
}

export function generationOfMutation(mutation: AnyMutation): number {
  return mutationOrigins.get(mutation)?.generation ?? getRecheckGeneration();
}

const queryOrigins = new WeakMap<AnyQuery, RequestOrigin>();

// A read belongs to the view that started its fetch, observed or not: the care searches use an
// imperative fetchQuery with no observer, yet the mounted view sent them. Called on every fetch start,
// so a refetch belongs to the view that started it; a read started with no view mounted belongs to none.
export function rememberQueryView(query: AnyQuery): void {
  queryOrigins.set(query, currentOrigin());
}

export function viewOfQuery(query: AnyQuery): Permission | null {
  return queryOrigins.get(query)?.view ?? null;
}

export function generationOfQuery(query: AnyQuery): number {
  return queryOrigins.get(query)?.generation ?? getRecheckGeneration();
}

const allButMe = {
  predicate: (query: { queryKey: readonly unknown[] }) => query.queryKey[0] !== 'me',
};

// A settled write still holds what was submitted («Editar datos» name, new titular). Once the server
// has refused a permission none of it stays in memory; a write still in flight is left to finish.
function dropSettledMutations(client: QueryClient): void {
  const mutations = client.getMutationCache();
  for (const mutation of mutations.getAll()) {
    if (mutation.state.status !== 'pending') mutations.remove(mutation);
  }
}

// The query keys carry no view prefix and few writes have a mutationKey, so a lost permission drops
// everything but /me: the other views are asked again on their next visit, and no «Usuario» PII nor
// submitted write payload stays in memory after the server said no.
function dropAllButMe(client: QueryClient): void {
  void client.cancelQueries(allButMe);
  client.removeQueries(allButMe);
  dropSettledMutations(client);
}

// Which view a refusal is about. The access-management resource, which predates the shared code,
// names it by its own code; otherwise it is the view that made the request. The panel never reads
// `detail`.
function refusedView(error: ApiError, source: RefusalSource): Permission | null {
  if (error.code === 'access_manage_required') return 'access.manage';
  return source.view;
}

export function handlePermissionRefusal(
  client: QueryClient,
  error: unknown,
  source: RefusalSource,
): void {
  if (!isPermissionRefused(error)) return;
  // The cache hears about a refused write before the write records its own error, so it is still
  // pending here and no sweep of settled writes would reach it: it goes by reference, payload and all.
  if (source.mutation !== undefined) client.getMutationCache().remove(source.mutation);
  // A request made by the previous person says nothing about whoever is signed in now: it closes no
  // view, drops nothing of theirs and re-reads no /me.
  if (source.generation !== undefined && source.generation !== getRecheckGeneration()) return;
  // Every refusal clears what was submitted, even one that latches nothing new. It cannot loop:
  // removing a mutation refetches nothing.
  dropSettledMutations(client);
  const view = refusedView(error, source);
  if (view === null) {
    // No view asked, so none is replaced and none is mounted to refetch: drop the cache and stop.
    dropAllButMe(client);
    return;
  }
  // A view already refused has stopped reading; another 403 for it re-reads nothing.
  if (!markViewRefused(view)) return;
  // A re-read already running answers for this view too: one shared promise, one /me read.
  if (isRecheckRunning()) return;
  void recheckAccess(client);
}

const ME = ['me'] as const;

// Asks the server again who this person is. Everything but /me goes first, so no view renders
// data the server may no longer allow while the answer is on its way.
export function recheckAccess(client: QueryClient): Promise<void> {
  const startedAt = Date.now();
  return beginRecheck((ticket) => {
    dropAllButMe(client);
    return client.refetchQueries({ queryKey: ME, type: 'all' }).then(() => {
      settleRecheck(ticket, () => {
        // A write that failed while /me was being read settled after the first purge.
        dropAllButMe(client);
        // refetchQueries swallows a failed read, so success is judged by a /me answer newer than
        // this re-read. findAll, not find: find matches the key exactly and /me is keyed by uid.
        const me = client.getQueryCache().findAll({ queryKey: ME })[0];
        const fresh = me?.state.status === 'success' && me.state.dataUpdatedAt >= startedAt;
        return fresh ? 'idle' : 'failed';
      });
    });
  });
}
